/**
 * useMediaAssets – query media_assets for a given owner.
 * Replaces all legacy image field reads.
 */

import { useQuery } from '@tanstack/react-query';
import { listAssetsByOwner, resolveAssetUrl } from '@/lib/media/mediaApi';
import type { MediaAsset, MediaKind, OwnerType, VariantWidth } from '@/lib/media/mediaTypes';

export function useMediaAssets(
  ownerType: OwnerType,
  ownerId: string | null | undefined,
  kind?: MediaKind,
  /** Pass null to get only base (non-seasonal) assets, a season string for that season, or undefined for all */
  seasonKey?: string | null,
) {
  return useQuery<MediaAsset[]>({
    queryKey: ['media-assets', ownerType, ownerId, kind, seasonKey],
    queryFn: () => listAssetsByOwner(ownerType, ownerId!, kind, seasonKey),
    enabled: !!ownerId,
    // Phase 1 stability: media assets change only via explicit upload/delete mutations,
    // which always call invalidateQueries on this key. A 5-min staleTime prevents
    // constant refetches on public hot pages (event detail, place detail) and reduces
    // DB read amplification under traffic bursts.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Get the cover image URL for an owner entity.
 * Returns null if no cover asset exists.
 * Defaults to base (non-seasonal) assets only.
 */
export function useCoverImageUrl(
  ownerType: OwnerType,
  ownerId: string | null | undefined,
  variant: VariantWidth = 640,
): string | null {
  const { data } = useMediaAssets(ownerType, ownerId, 'cover', null);
  if (!data || data.length === 0) return null;
  // Pick the newest cover asset (by created_at) to stay consistent with
  // MediaImageManager.activeCover which also picks newest.
  const sorted = data.length === 1
    ? data
    : [...data].sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
  return resolveAssetUrl(sorted[0], variant);
}

/**
 * Get gallery image URLs for an owner entity.
 * Defaults to base (non-seasonal) assets only.
 */
export function useGalleryUrls(
  ownerType: OwnerType,
  ownerId: string | null | undefined,
  variant: VariantWidth = 640,
): string[] {
  const { data } = useMediaAssets(ownerType, ownerId, 'gallery', null);
  if (!data) return [];
  return data
    .map((a) => resolveAssetUrl(a, variant))
    .filter(Boolean) as string[];
}
